import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  IconButton,
  Text,
  Input,
  VStack,
  HStack,
  Avatar,
  useColorModeValue,
  Collapse,
  Flex,
  Badge,
  Tooltip,
  Spinner,
  InputGroup,
  InputRightElement,
  Divider,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import {
  Robot,
  PaperPlaneTilt,
  MagicWand,
  X,
  Lightning,
  Info,
  LightbulbFilament,
  Broom
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCreateSite, CreateSiteContext } from '../../context/CreateSiteContext';
import aiService from '../../services/aiService'; // Import aiService

const MotionBox = motion(Box);

export const AIAssistant = ({ isVisible, onToggleVisibility }) => { // onToggleVisibility is now for parent to fully hide/show
  // Use the context directly to avoid the hook error if this component
  // is rendered somewhere without being wrapped in CreateSiteProvider
  const context = useContext(CreateSiteContext);
  
  // If context is not available, handle it gracefully
  if (!context) {
    console.error('AIAssistant must be used within a CreateSiteProvider');
    return null;
  }
  
  const { 
    siteData,
    currentStep,
    fetchAiSiteInfo, 
    aiLoading, 
    groundingInfo,
    setSiteData
  } = context;

  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  // const [isExpanded, setIsExpanded] = useState(false); // REMOVED
  const messagesEndRef = useRef(null);
  const toast = useToast(); // For feedback on clear

  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isPanelOpen, setIsPanelOpen] = useState(!isMobile); // For mobile: panel open or FAB
  const [isMinimized, setIsMinimized] = useState(false); // For desktop: panel open or FAB

  const bgColor = useColorModeValue('bg.default', 'bg.default'); // Changed
  const borderColor = useColorModeValue('gray.200', 'gray.700'); // This is used by initial message bubble, but panel uses "border.default"
  const headerColor = useColorModeValue('bg.accent', 'bg.accent');
  const textColor = useColorModeValue('text.default', 'text.default');
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const chatBgUser = useColorModeValue('bg.surface', 'bg.surface'); // Changed
  const chatBgBot = useColorModeValue('bg.surface', 'bg.surface'); // Changed

  // AI suggestions based on current step
  const suggestions = [
    {
      step: 0,
      text: 'Help me write a compelling description',
      actionType: 'chatQuery',
      initialOnly: true // Only show in the initial suggestions block
    },
    {
      step: 0,
      text: 'Tell me about this site',
      actionType: 'chatQuery'
    },
    {
      step: 1,
      text: 'Find best coordinates for this site',
      actionType: 'chatQuery'
    },
    { 
      step: 2, 
      text: 'Generate a timeline for this site',
      actionType: 'chatQuery'
    },
    { 
      step: 3, 
      text: 'Suggest features to tag for this site',
      actionType: 'chatQuery'
    },
  ];

  // Scroll to bottom of messages on update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]); // Removed isExpanded dependency

  // Handle sending a message (typed by user)
  const handleSendMessage = async () => {
    if (!message.trim()) return;

    if (!siteData.name?.trim()) {
      setConversation((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Please enter a site name in the 'Basic Info' step first before asking questions.",
          sender: 'assistant',
          isError: true,
        },
      ]);
      return;
    }

    const userMessageText = message;
    const userMessage = {
      id: Date.now(),
      text: userMessageText,
      sender: 'user',
    };

    const botThinkingMessageId = Date.now() + 1;
    setConversation((prev) => [
      ...prev,
      userMessage,
      {
        id: botThinkingMessageId,
        text: 'Thinking...',
        sender: 'assistant',
        isLoading: true,
      },
    ]);
    setMessage(''); // Clear input

    try {
      // Prepare conversation history for the API
      const apiConversationHistory = conversation.map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));
      apiConversationHistory.push({ role: 'user', content: userMessageText });
      
      const response = await aiService.processChatQuery(
        userMessageText,
        siteData, // from context
        {}, // sourcePriorities (optional)
        apiConversationHistory
      );

      setConversation((prev) =>
        prev.map((msg) =>
          msg.id === botThinkingMessageId
            ? {
                ...msg,
                text: response.data?.reply || "Sorry, I couldn't get a response.",
                isLoading: false
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Error processing chat query:', error);
      setConversation((prev) =>
        prev.map((msg) =>
          msg.id === botThinkingMessageId
            ? {
                ...msg,
                text: `Error: ${error.message || 'Failed to get response.'}`,
                isLoading: false,
                isError: true,
              }
            : msg
        )
      );
    }
  };

  // Handle suggestion click
  const handleSuggestion = async (suggestion) => {
    const suggestionText = suggestion.text;

    // Check if suggestion requires a site name
    const requiresSiteName = [
      'Tell me about this site',
      'Find best coordinates for this site',
      'Generate a timeline for this site',
      'Suggest features to tag for this site'
    ];

    if (!siteData.name?.trim() && requiresSiteName.includes(suggestionText)) {
      setConversation((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Please enter a site name in the 'Basic Info' step first before using this suggestion.",
          sender: 'assistant',
          isError: true,
        },
      ]);
      return;
    }

    if (suggestion.actionType === 'chatQuery') {
      await handleGenericSuggestionQuery(suggestionText);
    } else if (typeof suggestion.action === 'function') {
        // Original behavior for other suggestions (e.g., console.log or future specific actions)
        // Add suggestion to conversation
        const userMessage = {
          id: Date.now(),
          text: suggestionText,
          sender: 'user',
          isSuggestion: true
        };
        setConversation(prev => [...prev, userMessage]);
        
        try {
          // For suggestions like "Tell me about this site" that were previously fetchAiSiteInfo
          // but are now chatQuery, this block won't be hit for them.
          // This handles original placeholder actions.
          if (suggestion.text !== 'Tell me about this site') { // Ensure we don't call fetchAiSiteInfo by mistake
             await suggestion.action(); // This would be console.log for placeholders
          }
          
          const botMessage = {
            id: Date.now() + 1,
            text: `Suggestion "${suggestionText}" processed.`, // Generic for placeholders
            sender: 'assistant'
          };
          setConversation(prev => [...prev, botMessage]);
          if (isMobile && !isPanelOpen) {
            setIsPanelOpen(true);
          } else if (!isMobile && isMinimized) {
            setIsMinimized(false);
          }
        } catch (error) {
          const errorMessage = {
            id: Date.now() + 1,
            text: `Sorry, I couldn't process your request for "${suggestionText}": ${error.message}`,
            sender: 'assistant',
            isError: true
          };
          setConversation(prev => [...prev, errorMessage]);
        }
    }
  };

  // New function to handle generic suggestion queries via chat
  const handleGenericSuggestionQuery = async (suggestionText) => {
    // Add user message (the suggestion text) to conversation
    const userMessage = {
      id: Date.now(),
      text: suggestionText,
      sender: 'user',
      isSuggestion: true,
    };

    const botThinkingMessageId = Date.now() + 1;
    setConversation((prev) => [
      ...prev,
      userMessage,
      {
        id: botThinkingMessageId,
        text: 'Thinking...',
        sender: 'assistant',
        isLoading: true,
      },
    ]);

    try {
      const apiConversationHistory = conversation.map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));
      // Add the current suggestion as the latest user message in history
      apiConversationHistory.push({ role: 'user', content: suggestionText });

      const response = await aiService.processChatQuery(
        suggestionText,
        siteData,
        {},
        apiConversationHistory
      );

      setConversation((prev) =>
        prev.map((msg) =>
          msg.id === botThinkingMessageId
            ? {
                ...msg,
                text: response.data?.reply || "Sorry, I couldn't get a response.",
                isLoading: false
              }
            : msg
        )
      );

      if (isMobile && !isPanelOpen) {
        setIsPanelOpen(true);
      } else if (!isMobile && isMinimized) {
        setIsMinimized(false);
      }
    } catch (error) {
      console.error('Error processing suggestion chat query:', error);
      setConversation((prev) =>
        prev.map((msg) =>
          msg.id === botThinkingMessageId
            ? {
                ...msg,
                text: `Error: ${error.message || 'Failed to get response.'}`,
                isLoading: false,
                isError: true,
              }
            : msg
        )
      );
    }
  };


  const intro = `I can help you document this megalithic site. Just enter the site's name and I'll assist you with:
  • Finding historical information
  • Locating coordinates
  • Building the timeline
  • Suggesting features to tag`;

  if (!isVisible || !context) return null;
  
  // Additional check to ensure we're on the create site page
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const isCreateSitePage = pathname.includes('/create-site') || pathname.includes('/edit-draft');
    const isSiteProfilePage = pathname.includes('/site/') && !pathname.includes('/create-site');
    
    // Don't render on site profile pages or any non-create-site pages
    if (!isCreateSitePage || isSiteProfilePage) {
      return null;
    }
  }

  const showFabCondition = (isMobile && !isPanelOpen) || (!isMobile && isMinimized);

  const fabJsx = (
    <MotionBox
      key="ai-assistant-fab"
      position="fixed"
      bottom={{ base: 36, md: 20 }} // Consistent FAB positioning
      right={{ base: 4, md: 6 }}
      zIndex={1050}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-component="create-site-ai-assistant"
    >
      <Tooltip label="Open AI Assistant" placement="left">
        <IconButton
          icon={<LightbulbFilament size={24} weight="fill" />}
          borderRadius="xl"
          size="lg"
          variant="primary" // Changed
          boxShadow="lg"
          onClick={() => {
            if (isMobile) {
              setIsPanelOpen(true);
            } else {
              setIsMinimized(false); // Expand panel on desktop
            }
          }}
          aria-label="Open AI Assistant"
        />
      </Tooltip>
    </MotionBox>
  );

  const panelJsx = (
    <Box
      key="ai-assistant-panel"
      position="fixed"
      // Determine panel position based on whether it's mobile full screen or desktop standard
      bottom={isMobile ? 0 : 20}
      right={isMobile ? 0 : 6}
      width={isMobile ? "100%" : "350px"}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? "100vh" : "70vh"}
      zIndex={1050}
      display="flex"
      flexDirection="column"
      data-component="create-site-ai-assistant"
    >
      <MotionBox
        initial={{ opacity: 0, y: isMobile ? "100%" : 20 }} // Slide from bottom on mobile, from slightly below on desktop
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: isMobile ? "100%" : 20 }}
        transition={{ duration: 0.3 }}
        bg={bgColor}
        borderRadius={{ base: isMobile ? 0 : "xl", md: "xl" }} // No radius if mobile full screen
            boxShadow="lg"
            borderWidth="1px"
            borderColor="border.default"
            overflow="hidden"
            position="relative"
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%" // Ensure it takes full height when mobile and panel is open
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: useColorModeValue(0.05, 0.15),
              pointerEvents: 'none',
              bgImage: useColorModeValue("url('/textures/parchment.png')", "url('/textures/leather.png')"),
              bgRepeat: 'repeat',
              bgSize: '500px',
              mixBlendMode: useColorModeValue('multiply', 'overlay'),
              zIndex: 0
            }}
          >
            {/* Header */}
            <Flex 
              p={3} 
              bg={headerColor} 
              color={useColorModeValue('text.default', 'white')} 
              alignItems="center"
              justifyContent="space-between"
              // cursor removed, header click no longer toggles expand
              flexShrink={0}
            >
              <HStack>
                <Avatar 
                  size="sm" 
                  icon={<Robot size={18} weight="fill" />} 
                  bg="white" 
                  color={useColorModeValue('text.default', headerColor)}
                />
                <Box>
                  <Text fontWeight="bold" fontSize="sm" fontFamily="heading">
                    Assistant
                  </Text>
                  <Text fontSize="xs" opacity={0.8} fontFamily="body">
                    AI-powered research
                  </Text>
                </Box>
              </HStack>
              
              <HStack spacing={1}>
                <Tooltip label="Clear chat" placement="bottom">
                  <IconButton
                    icon={<Broom size={16} />}
                    size="sm"
                    variant="ghost"
                    color={useColorModeValue('text.default', 'white')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConversation([]);
                      toast({
                        title: "Chat cleared",
                        status: "info",
                        duration: 2000,
                        isClosable: true,
                      });
                    }}
                    aria-label="Clear chat"
                  />
                </Tooltip>
                <IconButton
                  icon={<X size={16} />}
                  size="sm"
                  variant="ghost"
                    color={useColorModeValue('text.default', 'white')}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMobile) {
                        setIsPanelOpen(false); // Collapse to FAB on mobile
                      } else {
                        setIsMinimized(true);  // Minimize to FAB on desktop
                      }
                    }}
                    aria-label="Close Assistant"
                  />
              </HStack>
            </Flex>
            
            {/* AI loading indicator */}
          {aiLoading && (
            <Box
              p={2}
              bg="bg.accent" // Changed
              borderBottomWidth={1}
              borderColor="border.light" // Changed
            >
              <HStack spacing={2}>
                <Spinner size="xs" color="brand.primary" />
                <Text fontSize="xs" color="text.default">
                  Researching information about {siteData.name}...
                </Text>
              </HStack>
            </Box>
          )}

            {/* Chat content: Takes remaining space and scrolls (Collapse removed) */}
            <VStack spacing={0} align="stretch" flexGrow={1} overflow="hidden">
                
                {/* Conversation */}
                <Box 
                  p={4} 
                  flexGrow={1} // Make conversation area take available space
                  overflowY="auto" 
                  bg="bg.default"
                >
                  {conversation.length === 0 ? (
                    // Initial welcome message
                    <VStack spacing={4} align="stretch" my={4}>
                      <VStack
                        bg={chatBgBot}
                        p={3}
                        borderRadius="lg"
                        align="start"
                        borderWidth="1px"
                        borderColor="border.default" // Changed
                      >
                        <HStack mb={1}>
                          <Avatar size="xs" bg={headerColor} icon={<Robot size={12} />} />
                          <Text fontSize="xs" fontWeight="bold" color={textColor} fontFamily="heading">Assistant</Text>
                        </HStack>
                        <Text fontSize="sm" color={textColor} whiteSpace="pre-line" fontFamily="body">
                          {intro}
                        </Text>
                      </VStack>
                      
                      <Divider borderStyle="dashed" />
                      
                      <Box>
                        <HStack mb={2}>
                          <Badge 
                            variant="journal" // Changed
                            borderRadius="full" 
                            px={2}
                          >
                            <HStack spacing={1}>
                              <MagicWand size={12} />
                              <Text fontSize="xs">Suggestions</Text>
                            </HStack>
                          </Badge>
                        </HStack>
                        
                        <VStack align="stretch" spacing={2}>
                          {suggestions.filter(s => s.step === currentStep && s.initialOnly === true).map((suggestion, index) => (
                            <Button
                              key={index}
                              size="sm"
                              variant="outline"
                              justifyContent="flex-start"
                              leftIcon={<Lightning size={14} />}
                              onClick={() => handleSuggestion(suggestion)}
                              isDisabled={aiLoading}
                              // _hover removed to use theme's default for variant="outline"
                            >
                              {suggestion.text}
                            </Button>
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  ) : (
                    // Message history
                    <VStack spacing={4} align="stretch">
                      {conversation.map((msg) => (
                        <Box 
                          key={msg.id} 
                          alignSelf={msg.sender === 'user' ? 'flex-end' : 'flex-start'}
                          maxWidth="85%"
                        >
                          {msg.sender === 'user' ? (
                            // User message
                            <Box
                              bg={msg.isSuggestion ? useColorModeValue('brand.primary.100', 'brand.primary.800') : chatBgUser}
                              p={3}
                              borderRadius="lg"
                              borderBottomRightRadius={0}
                            >
                              <Text fontSize="sm" color={textColor} fontFamily="body">{msg.text}</Text>
                            </Box>
                          ) : (
                            // Bot message
                            <VStack
                              bg={chatBgBot}
                              p={3}
                              borderRadius="lg"
                              borderBottomLeftRadius={0}
                              align="start"
                              borderWidth="1px"
                              borderColor={msg.isError ? 'red.300' : "border.default"}
                            >
                              <HStack mb={1}>
                                <Avatar size="xs" bg={headerColor} icon={<Robot size={12} />} />
                                <Text fontSize="xs" fontWeight="bold" color={textColor} fontFamily="heading">Assistant</Text>
                              </HStack>
                              
                              {msg.isLoading ? (
                                <HStack>
                                  <Spinner size="xs" />
                                  <Text fontSize="sm" color={textColor} fontFamily="body">Thinking...</Text>
                                </HStack>
                              ) : (
                                <Text fontSize="sm" color={msg.isError ? 'red.500' : textColor} fontFamily="body">
                                  {msg.text}
                                </Text>
                              )}
                            </VStack>
                          )}
                        </Box>
                      ))}
                      <Box ref={messagesEndRef} />
                    </VStack>
                  )}
                </Box> {/* End Conversation Box */}
                
                {/* Suggestions */}
                <Box p={2} bg="bg.default" flexShrink={0}>
                  <HStack spacing={1} overflowX="auto" py={1} css={{
                    '&::-webkit-scrollbar': { height: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: useColorModeValue('rgba(0,0,0,0.2)', 'rgba(255,255,255,0.2)'),
                      borderRadius: '3px'
                    }
                  }}>
                    {suggestions
                      .filter(s => s.step === currentStep && s.initialOnly !== true) // Explicitly exclude initialOnly suggestions
                      .map((suggestion, index) => (
                        <Button
                          key={index}
                          size="xs"
                          variant="outline"
                          flexShrink={0}
                          onClick={() => handleSuggestion(suggestion)}
                          isDisabled={aiLoading}
                        >
                          {suggestion.text}
                        </Button>
                    ))}
                  </HStack>
                </Box> {/* End Suggestions Box */}
                
                {/* Input area */}
                <Box p={3} borderTopWidth="1px" borderColor="border.default" flexShrink={0}>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                  }}>
                    <InputGroup size="md">
                      <Input
                        placeholder="Ask me about this site..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        pr="3.5rem"
                        variant="journal"
                        isDisabled={aiLoading}
                        fontFamily="body"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          isRound
                          h="1.8rem"
                          w="1.8rem"
                          minW="1.8rem"
                          size="sm"
                          icon={<PaperPlaneTilt size={14} />}
                          isLoading={aiLoading}
                          type="submit"
                          isDisabled={!message.trim()}
                          variant="ghost"
                          colorScheme="yellow"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </form>
                </Box> {/* End Input area Box */}
            </VStack> {/* End VStack wrapping content */}
          </MotionBox>
    </Box>
  );

  return (
    <AnimatePresence mode="wait">
      {showFabCondition ? fabJsx : panelJsx}
    </AnimatePresence>
  );
};
